import React, { FC, useCallback, useEffect, useRef, useState } from 'react'
import parse from 'html-react-parser'
import { cn } from 'shared/utils'

import { StoryArticleEmbeded, StoryToggle } from '..'
import { uploadScript } from 'shared/utils/uploadScript'
import { useNewTagAdded, usePrevious } from 'shared/hooks'

import { POST_ARTICLE_HEIGHT } from 'modules/Feed/consts'
import { isEmpty } from 'shared/utils/isEmpty'

import { StoryArticleProps } from './StoryArticle.props'
import './StoryArticle.scss'
import { ConditionRender } from 'components'
import { useStory } from '../../serviceHooks'
import { useStoryContext } from 'components/Story/StoryContext'

export const StoryArticle: FC<StoryArticleProps> = ({
  dataTestPrefix,
  storyRef
}) => {
  const story = useStoryContext()

  const {
    isCollapsed,
    handleToggleCollapsed,
    toggleBigCard,
    article,
    isBigCard,
    preloaded,
    hasTiktok,
    isTiktokOpened,
    setPreloaded,
    setTouched
  } = useStory({ story, storyRef })

  const hasSocial = hasTiktok
  const isSocialPreloaded = isTiktokOpened
  const toggleCollapsed = handleToggleCollapsed

  const articleRef = useRef<HTMLDivElement>(null)
  const [openedEmbeded, setOpenedEmbeded] = useState<string[]>([])

  const prevIsCollapsed = usePrevious(isCollapsed)
  const isIFrameAdded = useNewTagAdded(articleRef, 'IFRAME')

  const toggleCollapsedHandler = useCallback(() => {
    setTouched(true)

    return isBigCard ? toggleCollapsed(!isCollapsed) : () => {}
  }, [isBigCard, isCollapsed, setTouched, toggleCollapsed])

  const expandHandler = useCallback(
    () => (isBigCard && isCollapsed ? toggleCollapsed(false) : () => {}),
    [isBigCard, isCollapsed, toggleCollapsed]
  )

  const onLoadTikTok = useCallback(() => {
    setPreloaded(false)

    if (isIFrameAdded) {
      setPreloaded(true)
    }
  }, [isIFrameAdded, setPreloaded])

  const options = {
    replace: ({ attribs }: any) => {
      if (attribs?.['data-embeded-response'] && !isEmpty(attribs?.['data-embeded-response'])) {
        let data = {
          thumbnail_url: '',
          thumbnail_width: 0,
          provider_url: '',
          author_name: '',
          provider_name: '',
          title: ''
        }

        try {
          data = JSON.parse(attribs['data-embeded-response'])
        } catch (e) {
          console.log(e)
        }
        const onOpen = () => {
          setOpenedEmbeded([...openedEmbeded, attribs?.id || data.thumbnail_url])
        }

        if (!openedEmbeded.includes(attribs?.id || data.thumbnail_url)) {
          return <StoryArticleEmbeded data={data} onOpen={onOpen} />
        }
      }
    }
  }

  useEffect(() => {
    uploadScript('twitter-tweet', 'https://platform.twitter.com/widgets.js')
    uploadScript('instagram-media', 'https://www.instagram.com/embed.js')
  }, [])

  useEffect(() => {
    if ((hasSocial && ((prevIsCollapsed && !isCollapsed) || isSocialPreloaded)) || openedEmbeded.length) {
      uploadScript('tiktok-embed', 'https://www.tiktok.com/embed.js', onLoadTikTok)
    }
  }, [onLoadTikTok, isCollapsed, prevIsCollapsed, hasSocial, isSocialPreloaded, openedEmbeded])

  useEffect(() => {
    if (
      !isBigCard &&
      !isCollapsed &&
      (hasSocial || (articleRef.current?.clientHeight && articleRef.current?.clientHeight > POST_ARTICLE_HEIGHT))
    ) {
      toggleCollapsed(true)
      toggleBigCard(true)
    }
  }, [
    articleRef,
    articleRef.current?.clientHeight,
    toggleBigCard,
    toggleCollapsed,
    preloaded,
    isCollapsed,
    isBigCard,
    hasSocial
  ])

  return (
    <div
      ref={articleRef}
      className={cn('story-article', {
        'story-article--collapsed': isCollapsed,
        'story-article--collapsed--pinned': isCollapsed && story.pinned,
        'story-article--collapsed--social': isCollapsed && hasSocial
      })}
      onClick={expandHandler}
    >
      <ConditionRender
        condition={isBigCard}
        component={
          <StoryToggle
            isCollapsed={isCollapsed}
            toggleCollapsedHandler={toggleCollapsedHandler}
            dataTestPrefix={`${dataTestPrefix}`}
          />
        }
      />
      {
        story.title ? (
          <h5 className="story__body-title" data-test={`${dataTestPrefix}-article_title`}>
            {story.title}
          </h5>
        ) : null
      }
      <div className="story-article-txt ql-editor ql-render" data-test={`${dataTestPrefix}-content`}>
        {parse(article, options)}
      </div>
    </div>
  )
}
